import React from 'react';
import { Modal, Result, Button, Divider, Tag } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, RedoOutlined, ArrowRightOutlined, BookOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { toTenScale } from '../../learning/utils/moduleGate.js';

export function QuizResultModal({ open, result, onRetry, courseId, lessonId }) {
  const navigate = useNavigate();

  if (!result) return null;

  const { score, passed, correctCount, totalQuestions, feedback, evaluatedAnswers = [], completedLessonId, courseId: resCourseId } = result;
  const activeCourseId = courseId || resCourseId;
  const targetLessonId = completedLessonId || lessonId;

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      width={720}
      closable={false}
      centered
      style={{ borderRadius: 12, overflow: 'hidden' }}
    >
      <div style={{ padding: '20px 8px' }}>
        {/* Result Header */}
        <Result
          status={passed ? 'success' : 'error'}
          title={
            <div style={{ fontSize: 22, fontWeight: 800, color: passed ? '#15803d' : '#b91c1c' }}>
              {passed ? '🎉 Chúc Mừng! Bạn Đã Vượt Qua Bài Kiểm Tra' : '❌ Bạn Chưa Đạt Điểm Yêu Cầu'}
            </div>
          }
          subTitle={
            <div style={{ fontSize: 14, color: '#475569', marginTop: 4 }}>
              {feedback}
            </div>
          }
          extra={[
            <div
              key="score-box"
              style={{
                display: 'inline-flex',
                gap: 24,
                padding: '12px 28px',
                background: '#f8fafc',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                marginBottom: 16,
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: '#64748b' }}>Điểm số của bạn</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: passed ? '#16a34a' : '#dc2626' }}>
                  {toTenScale(score)}/10
                </div>
              </div>
              <Divider type="vertical" style={{ height: 40 }} />
              <div>
                <div style={{ fontSize: 12, color: '#64748b' }}>Số câu trả lời đúng</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a' }}>
                  {correctCount} / {totalQuestions}
                </div>
              </div>
              <Divider type="vertical" style={{ height: 40 }} />
              <div>
                <div style={{ fontSize: 12, color: '#64748b' }}>Điểm đạt tối thiểu</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#64748b' }}>
                  Trên 5/10
                </div>
              </div>
            </div>,
            passed && (
              <div key="auto-completed-badge" style={{ marginBottom: 12 }}>
                <Tag color="success" icon={<CheckCircleFilled />} style={{ padding: '4px 14px', fontSize: 13, borderRadius: 6 }}>
                  Hệ thống đã tự động ghi nhận HOÀN THÀNH bài học này vào tiến độ học tập của bạn!
                </Tag>
              </div>
            ),
          ]}
        />

        {/* Detailed Question Review */}
        <div style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto', paddingRight: 8 }}>
          <h4 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>
            📋 Chi tiết đáp án & Lời giải thích:
          </h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {evaluatedAnswers.map((ans, idx) => (
              <div
                key={ans.questionId || idx}
                style={{
                  padding: '12px 16px',
                  borderRadius: 8,
                  background: ans.isCorrect ? '#f0fdf4' : '#fef2f2',
                  border: ans.isCorrect ? '1px solid #bbf7d0' : '1px solid #fecaca',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                  {ans.isCorrect ? (
                    <CheckCircleFilled style={{ color: '#16a34a', fontSize: 16, marginTop: 2 }} />
                  ) : (
                    <CloseCircleFilled style={{ color: '#dc2626', fontSize: 16, marginTop: 2 }} />
                  )}
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                    Câu {idx + 1}: {ans.prompt}
                  </span>
                </div>

                <div style={{ fontSize: 13, color: '#475569', marginLeft: 24 }}>
                  <div>
                    Bạn chọn:{' '}
                    <strong>
                      {ans.options?.[ans.selectedOptionIndex]?.text || 'Chưa chọn'}
                    </strong>
                  </div>
                  {!ans.isCorrect && (
                    <div style={{ color: '#15803d', fontWeight: 600, marginTop: 2 }}>
                      Đáp án đúng: {ans.options?.[ans.correctOptionIndex]?.text}
                    </div>
                  )}
                  {ans.explanation && (
                    <div style={{ fontStyle: 'italic', marginTop: 4, color: '#64748b' }}>
                      💡 Giải thích: {ans.explanation}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
          {/* Hết lượt làm bài (maxAttempts) thì máy chủ trả canRetry = false */}
          {!passed && result?.canRetry !== false && (
            <Button icon={<RedoOutlined />} onClick={onRetry}>
              Làm lại bài test
            </Button>
          )}

          {targetLessonId && activeCourseId && (
            <Button
              icon={<BookOutlined />}
              onClick={() => navigate(`/learn/${activeCourseId}/lessons/${targetLessonId}`)}
            >
              Về bài học
            </Button>
          )}

          <Button
            type="primary"
            icon={passed ? <ArrowRightOutlined /> : undefined}
            onClick={() => {
              if (activeCourseId) {
                navigate(`/courses/${activeCourseId}`);
              } else {
                navigate('/dashboard');
              }
            }}
            style={passed ? { background: '#0284c7', borderColor: '#0284c7' } : {}}
          >
            {passed ? 'Xem danh sách bài học' : 'Ôn lại lý thuyết'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
